// ════════════════════════════════════════════════════════════════════════════
// useFocusTrap — trap de foco dentro de um container (Modal)
// ════════════════════════════════════════════════════════════════════════════

import { useEffect, useRef } from "react";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

/**
 * Mantém o foco preso dentro do container enquanto enabled === true.
 * Foca o primeiro elemento focável ao ativar; restaura foco ao trigger ao
 * desativar (caller passa lastFocused via closure ou ref se quiser).
 *
 * @returns ref para anexar ao container do modal
 */
export function useFocusTrap<T extends HTMLElement = HTMLDivElement>(
  enabled: boolean
): React.RefObject<T> {
  const ref = useRef<T>(null);
  const previousActive = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!enabled) return;
    previousActive.current = document.activeElement as HTMLElement | null;

    const container = ref.current;
    if (!container) return;

    // Foca o primeiro elemento focável
    const focusables = container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
    const first = focusables[0];
    if (first) first.focus();

    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== "Tab" || !container) return;
      const items = Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const firstEl = items[0];
      const lastEl = items[items.length - 1];
      const active = document.activeElement;

      if (e.shiftKey && active === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && active === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("keydown", onKeyDown);
      // Restaura foco
      if (previousActive.current && document.contains(previousActive.current)) {
        previousActive.current.focus();
      }
    };
  }, [enabled]);

  return ref;
}
